import React, { useState, useEffect } from "react";
import axios from "axios";
import AdminSidebar from "../components/AdminSidebar";

// Icons from react-icons
import { FaColumns } from "react-icons/fa";
import { ModifyColumns } from "../components/ModifyColumn";

// Main Dashboard Component
const Dashboard = () => {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showColumnsMenu, setShowColumnsMenu] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10); // State for items per page
  const [columns, setColumns] = useState([
    {
      id: "1",
      key: "date",
      title: "Date",
      visible: true,
      locked: true,
      category: "Schedule Details",
      width: "w-1/12", 
    },
    {
      id: "2",
      key: "email",
      title: "Email",
      visible: true,
      locked: false,
      category: "User Information",
      width: "w-3/12",
    },
    {
      id: "3",
      key: "username",
      title: "Username",
      visible: true,
      locked: false,
      category: "User Information",
      width: "w-2/12", 
    },
    {
      id: "4",
      key: "subscription",
      title: "Subscription",
      visible: true,
      locked: false,
      category: "Subscription Details",
      width: "w-1/12",
    },
    {
      id: "5",
      key: "start_date",
      title: "Start Date",
      visible: true,
      locked: false,
      category: "Subscription Details",
      width: "w-1/12", 
    },
    {
      id: "6",
      key: "end_date",
      title: "End Date",
      visible: true,
      locked: false,
      category: "Subscription Details",
      width: "w-1/12", 
    },
    {
      id: "7",
      key: "days_remaining",
      title: "Days Remaining",
      visible: true,
      locked: false,
      category: "Subscription Details",
      width: "w-1/12",
    },
    {
      id: "8",
      key: "country",
      title: "Country",
      visible: true,
      locked: false,
      category: "User Information",
      width: "w-1/12", 
    },
  ]);

  useEffect(() => {
    const fetchSchedules = async () => {
      setLoading(true);
      try {
        const response = await axios.post(
          "https://api.confidanto.com/user/users-info"
        );
        setSchedules(response.data);
      } catch (err) {
        setError("Failed to fetch data.");
        console.error("Error fetching schedules:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchSchedules();
  }, []);

  const handleDelete = async (id) => {
    try {
      await axios.delete(`http://localhost:5000/schedule-call/${id}`);
      setSchedules(schedules.filter((schedule) => schedule.id !== id));
    } catch (err) {
      console.error("Error deleting schedule:", err);
      setError("Failed to delete schedule.");
    }
  };

  const renderTableCell = (schedule, column) => {
    if (column.key === "actions") {
      return (
        <button
          onClick={() => handleDelete(schedule.id)}
          className="text-red-600 hover:text-red-800"
        >
          Delete
        </button>
      );
    }
    const cellData = schedule[column.key];
    return (
      <span className="truncate" title={cellData}>
        {cellData}
      </span>
    );
  };

  // Pagination Logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = schedules.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(schedules.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (event) => {
    setItemsPerPage(Number(event.target.value));
    setCurrentPage(1); // Reset to the first page
  };

  return (
    <div className="flex bg-gray-50 min-h-screen">
      <AdminSidebar />
      <main className="flex-1 p-6 min-w-0">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-800">Dashboard</h1>
          <div className="relative">
            <button
              className="bg-white text-gray-700 px-4 py-2 rounded-lg shadow-sm hover:bg-gray-100 flex items-center gap-2"
              onClick={() => setShowColumnsMenu(!showColumnsMenu)}
            >
              <FaColumns />
              <span>Columns</span>
            </button>
            {showColumnsMenu && (
              <ModifyColumns
                columns={columns}
                setColumns={setColumns}
                setShowColumnsMenu={setShowColumnsMenu}
              />
            )}
          </div>
        </div>

        {loading && <p>Loading...</p>}
        {error && <p className="text-red-500">{error}</p>}

        {!loading && !error && (
          <>
            <div className="bg-white shadow-md rounded-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white table-fixed">
                  <thead class="bg-[#4142dc] text-white">
                    <tr>
                      {columns
                        .filter((c) => c.visible)
                        .map((column) => (
                          <th
                            key={column.key}
                            className={`${column.width} py-3 px-4 border-b text-left text-sm font-medium text-white uppercase tracking-wider`}
                          >
                            {column.title}
                          </th>
                        ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {currentItems.map((schedule) => (
                      <tr
                        key={schedule.id}
                        className="hover:bg-gray-100 transition-colors"
                      >
                        {columns
                          .filter((c) => c.visible)
                          .map((column) => (
                            <td
                              key={column.key}
                              className="py-3 px-4 text-sm text-gray-700 whitespace-nowrap overflow-hidden text-ellipsis"
                            >
                              {renderTableCell(schedule, column)}
                            </td>
                          ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex justify-between items-center mt-4">
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-600">
                  Showing {schedules.length > 0 ? indexOfFirstItem + 1 : 0} to{" "}
                  {Math.min(indexOfLastItem, schedules.length)} of{" "}
                  {schedules.length} results
                </span>
                <div className="flex items-center gap-2">
                  <label
                    htmlFor="rows-per-page"
                    className="text-sm text-gray-600 sr-only"
                  >
                    Rows per page:
                  </label>
                  <select
                    id="rows-per-page"
                    value={itemsPerPage}
                    onChange={handleItemsPerPageChange}
                    className="block w-full pl-3 pr-8 py-1.5 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                  >
                    <option value={5}>5 rows</option>
                    <option value={10}>10 rows</option>
                    <option value={15}>15 rows</option>
                    {/* <option value={100}>100 rows</option> */}
                  </select>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1 bg-white border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-600">
                  Page {currentPage} of {totalPages > 0 ? totalPages : 1}
                </span>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages || totalPages === 0}
                  className="px-3 py-1 bg-white border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default Dashboard;
